import React, { Fragment } from "react";

import { arrayOf, object, bool, func, number, array, any } from "prop-types";

import TimesheetRow from "../TimesheetRow/index";

import { dayNames as days } from "../../helpers";

import * as style from "./styles.module.scss";
import TimesheetColumnTotalsRow from "../TimesheetColumnTotalsRow/index";
import TimesheetColumnTotalTimeFrameRow from "../TimesheetColumnTotalTimeFrameRow";
import TimesheetCell from "../TimesheetCell/index";
import { decimalTohhmm } from "../../helpers/helpers";
import { displayTimesheetDate } from "../../helpers/timesheets";

const makeRows = ({ rates, columns }) =>
  rates.map((rate, rateIndex) =>
    days.map((day, dayIndex) => columns[dayIndex].StandardPayQty[rateIndex])
  );

const returnDays = (
  columnError,
  bookedDay,
  nonAttendanceCode,
  sum,
  nonAttendanceCodes = {}
) => {
  let valueToReturn;
  if (columnError.over24hrs && bookedDay) {
    valueToReturn = <b>Can&apos;t exceed 24 hours</b>;
  } else if (!bookedDay) {
    valueToReturn = <span className="font-weight-bold">Not booked</span>;
  } else if (nonAttendanceCode !== null) {
    valueToReturn = (
      <span className="font-weight-bold">
        {nonAttendanceCodes[nonAttendanceCode]}
      </span>
    );
  } else {
    valueToReturn = decimalTohhmm(sum);
  }
  return valueToReturn;
};

const dayTypeTotals = (
  columnError,
  bookedDay,
  nonAttendanceCode,
  sum,
  nonAttendanceCodes = {}
) => {
  let valueToReturn;
  if (!bookedDay) {
    valueToReturn = <span className="font-weight-bold">Not booked</span>;
  } else if (nonAttendanceCode !== null) {
    valueToReturn = (
      <span className="font-weight-bold">
        {nonAttendanceCodes[nonAttendanceCode]}
      </span>
    );
  } else if (sum === 1) {
    valueToReturn = <b>Worked</b>;
  } else if (sum === 0) {
    valueToReturn = "";
  }
  return valueToReturn;
};

export const getSalaryOption = (type) => {
  switch (type) {
    case "H":
      return "per hour";
    case "D":
      return "per day";
    default:
      return <Fragment></Fragment>;
  }
};

// eslint-disable-next-line import/prefer-default-export
export const TimesheetTable = ({
  timesheet,
  isEditing,
  updateHour,
  updateStartTime,
  updateEndTime,
  columnErrors,
  columnTotals,
  timeTotals,
  bookedDays,
  nonAttendanceDays,
  startDate,
  setFormTouched,
  nonAttendanceCodes,
}) => {
  // On first render timesheetState is not populated, so we use `timesheet` instead
  const data = makeRows(timesheet);

  const columnTotalsSummed = columnTotals.reduce((acc, curr) => acc + curr);
  (() => columnTotalsSummed > 0.01 && setFormTouched(true))();

  const timeFramesTotalsSummed =
    timeTotals.length === 0 ? 0 : timeTotals.reduce((acc, curr) => acc + curr);
  (() => timeFramesTotalsSummed > 0.01 && setFormTouched(true))();

  const makeTimeFrameRows = ({ timeFrames }, timeType) =>
    timeFrames.map((entry) => entry[timeType] || 0);

  const timeFrameMethod = timesheet.timesheetFillOption === "timeFrame";
  const timeFramesFilled = timesheet.timeFrames.length !== 0;

  const showTimeFramesOption = () => {
    if (timesheet.submitted) {
      if ((timeFramesTotalsSummed === 0 && !isEditing) || !timeFrameMethod) {
        return false;
      }
      return true;
    }
    if (timeFrameMethod) {
      return true;
    }
    return false;
  };

  return showTimeFramesOption() && timeFramesFilled ? (
    <Fragment>
      <table className="d-none d-lg-table w-100">
        <thead>
          <tr>
            <th className={`${style.rate} font-weight-bold`} />
            {days.map((day, i) => (
              <th key={day}>
                <span className="text-uppercase font-weight-bold">
                  {day.slice(0, 3)}
                </span>{" "}
                {displayTimesheetDate(startDate, i)}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          <TimesheetRow
            rate={"Start time"}
            days={makeTimeFrameRows(timesheet, "startTime")}
            isEditing={isEditing}
            columnErrors={columnErrors}
            bookedDays={bookedDays}
            nonAttendanceDays={nonAttendanceDays}
            onEdit={isEditing ? updateStartTime : () => {}}
            rowType={timesheet.temporaryVacancyTimeUnitCode}
            getSalaryOption={getSalaryOption}
            timeFrame={true}
          />
          <TimesheetRow
            rate={"End time"}
            days={makeTimeFrameRows(timesheet, "endTime")}
            isEditing={isEditing}
            columnErrors={columnErrors}
            bookedDays={bookedDays}
            nonAttendanceDays={nonAttendanceDays}
            onEdit={isEditing ? updateEndTime : () => {}}
            rowType={timesheet.temporaryVacancyTimeUnitCode}
            getSalaryOption={getSalaryOption}
            timeFrame={true}
          />
          <TimesheetColumnTotalTimeFrameRow
            totals={timeTotals}
            columnErrors={columnErrors}
            bookedDays={bookedDays}
            nonAttendanceDays={nonAttendanceDays}
            nonAttendanceCodes={nonAttendanceCodes}
            returnDays={returnDays}
          />
        </tbody>
      </table>
      <div className="d-lg-none" style={{ overflowX: "auto" }}>
        <table style={{ overflow: "auto" }} className="d-table w-100">
          <thead>
            <tr>
              <th className={`${style.rate} font-weight-bold`} />
              {days.map((day, i) => (
                <th key={day}>
                  <span className="text-uppercase font-weight-bold">
                    {day.slice(0, 3)}
                  </span>{" "}
                  {displayTimesheetDate(startDate, i)}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            <TimesheetRow
              rate={"Start time"}
              days={makeTimeFrameRows(timesheet, "startTime")}
              isEditing={isEditing}
              columnErrors={columnErrors}
              bookedDays={bookedDays}
              nonAttendanceDays={nonAttendanceDays}
              onEdit={isEditing ? updateStartTime : () => {}}
              rowType={timesheet.temporaryVacancyTimeUnitCode}
              getSalaryOption={getSalaryOption}
              timeFrame={true}
            />
            <TimesheetRow
              rate={"End time"}
              days={makeTimeFrameRows(timesheet, "endTime")}
              isEditing={isEditing}
              columnErrors={columnErrors}
              bookedDays={bookedDays}
              nonAttendanceDays={nonAttendanceDays}
              onEdit={isEditing ? updateEndTime : () => {}}
              rowType={timesheet.temporaryVacancyTimeUnitCode}
              getSalaryOption={getSalaryOption}
              timeFrame={true}
            />
            <TimesheetColumnTotalTimeFrameRow
              totals={timeTotals}
              columnErrors={columnErrors}
              bookedDays={bookedDays}
              nonAttendanceDays={nonAttendanceDays}
              nonAttendanceCodes={nonAttendanceCodes}
              returnDays={returnDays}
            />
          </tbody>
        </table>
      </div>
    </Fragment>
  ) : (
    <Fragment>
      <table className="d-none d-lg-table w-100">
        <thead>
          <tr>
            <th className={`${style.rate} font-weight-bold`}>Rate</th>
            {days.map((day, i) => (
              <th key={day}>
                <span className="text-uppercase font-weight-bold">
                  {day.slice(0, 3)}
                </span>{" "}
                {displayTimesheetDate(startDate, i)}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {data.map((row, i) => (
            <TimesheetRow
              key={i}
              rateIdx={i}
              rate={timesheet.rates[i]}
              days={row}
              isEditing={isEditing}
              columnErrors={columnErrors}
              bookedDays={bookedDays}
              nonAttendanceDays={nonAttendanceDays}
              onEdit={isEditing ? updateHour : () => {}}
              rowType={timesheet.temporaryVacancyTimeUnitCode}
              getSalaryOption={getSalaryOption}
            />
          ))}
          <TimesheetColumnTotalsRow
            columnTotals={columnTotals}
            columnErrors={columnErrors}
            bookedDays={bookedDays}
            nonAttendanceDays={nonAttendanceDays}
            nonAttendanceCodes={nonAttendanceCodes}
            returnDays={returnDays}
            rowType={timesheet.temporaryVacancyTimeUnitCode}
            dayTypeTotals={dayTypeTotals}
          />
        </tbody>
      </table>
      <div className={`d-lg-none ${style.mobileTimesheet__day}`}>
        {days.map((day, dayIdx) => (
          <Fragment key={day}>
            <header
              className={`${style.mobileTimesheet__header} ${
                bookedDays[dayIdx] ? "" : style.notBooked
              }`}
            >
              <div>
                <span className="text-uppercase font-weight-bold">{day}</span>{" "}
                {displayTimesheetDate(startDate, dayIdx)}
              </div>
              <div>
                <span
                  className={`text-uppercase font-weight-bold
                ${style.mobileTimesheet__dayTotal}
                ${
                  Object.values(columnErrors[dayIdx]).includes(true)
                    ? ""
                    : "d-none"
                }
                ${
                  !Object.values(columnErrors[dayIdx]).includes(true)
                    ? ""
                    : "d-none"
                }
                `}
                >
                  total:&nbsp;
                </span>
                {timesheet.temporaryVacancyTimeUnitCode === "H"
                  ? returnDays(
                      columnErrors[dayIdx],
                      bookedDays[dayIdx],
                      nonAttendanceDays[dayIdx],
                      columnTotals[dayIdx],
                      nonAttendanceCodes
                    )
                  : dayTypeTotals(
                      columnErrors[dayIdx],
                      bookedDays[dayIdx],
                      nonAttendanceDays[dayIdx],
                      columnTotals[dayIdx],
                      nonAttendanceCodes
                    )}
              </div>
            </header>
            <div
              className={`${style.mobileTimesheet__rates}
             ${bookedDays[dayIdx] ? "" : "d-none"}`}
            >
              {data.map((row, index) => {
                const isValid =
                  !Object.values(columnErrors[dayIdx]).includes(true) &&
                  !Number.isNaN(parseFloat(row[dayIdx], 10)) &&
                  bookedDays[dayIdx];
                return (
                  <div
                    key={index}
                    className={`d-flex justify-content-between ${style.mobileTimesheet__rate}`}
                  >
                    <div className="d-flex justify-content-center flex-wrap align-items-center">
                      <div
                        className={`font-weight-bold ${style.mobileTimesheet__rateDisplayName}`}
                      >
                        {timesheet.rates[index].displayName}
                      </div>
                      <div>
                        (£{timesheet.rates[index].payment}{" "}
                        {getSalaryOption(
                          timesheet.temporaryVacancyTimeUnitCode
                        )}
                        )
                      </div>
                    </div>
                    <div
                      className="align-items-center d-flex"
                      key={`day_${index}_${dayIdx}`}
                    >
                      <TimesheetCell
                        hours={data[index][dayIdx]}
                        isEditing={isEditing}
                        bookedDay={bookedDays[dayIdx]}
                        nonAttendanceCode={nonAttendanceDays[dayIdx]}
                        handleChange={
                          isEditing
                            ? (newValue) => updateHour(index, dayIdx, newValue)
                            : () => {}
                        }
                        error={!isValid}
                        cellType={timesheet.temporaryVacancyTimeUnitCode}
                      />
                    </div>
                  </div>
                );
              })}
            </div>
          </Fragment>
        ))}
      </div>
    </Fragment>
  );
};

TimesheetTable.propTypes = {
  timesheet: object,
  isEditing: bool.isRequired,
  updateHour: func.isRequired,
  columnErrors: array.isRequired,
  columnTotals: arrayOf(number).isRequired,
  bookedDays: array.isRequired,
  nonAttendanceDays: array.isRequired,
  startDate: any.isRequired,
  setFormTouched: func,
  nonAttendanceCodes: object,
};
